"use client";

import React from "react";
import { useTranslate } from "../../lib/translation";
import styles from "./Progress.module.css";

export interface ProgressProps {
  /** Current progress value, scaled against max and clamped to 0..max. @default 0 */
  value?: number;
  /** Maximum value. @default 100 */
  max?: number;
  /** Accessible name for the progressbar. Defaults to the translated "Progress" (`progress.label`). */
  label?: string;
  /** Track height token. @default "md" */
  size?: "sm" | "md" | "lg";
  /** Semantic fill color. @default "neutral" */
  state?: "neutral" | "success" | "info" | "warning" | "error";
  /** Unknown duration: animates a sweeping bar and drops aria-valuenow. */
  indeterminate?: boolean;
  /** Optional utility classes on the root. */
  className?: string;
}

/** Linear progress bar with optional label, semantic state colors, and an indeterminate mode. */
const Progress: React.FC<ProgressProps> = ({
  value = 0,
  max = 100,
  label,
  size = "md",
  state = "neutral",
  indeterminate = false,
  className,
}) => {
  const t = useTranslate();
  const clamped = Math.min(max, Math.max(0, value));
  const pct = max > 0 ? (clamped / max) * 100 : 0;

  return (
    <div
      className={[styles.root, styles[size], className].filter(Boolean).join(" ")}
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={max}
      aria-valuenow={indeterminate ? undefined : Math.round(clamped)}
      aria-label={label ?? t("progress.label", "Progress")}
    >
      <div
        className={[
          styles.bar,
          styles[state],
          indeterminate ? styles.indeterminate : "",
        ]
          .filter(Boolean)
          .join(" ")}
        style={indeterminate ? undefined : { transform: `scaleX(${pct / 100})` }}
      />
    </div>
  );
};

export default Progress;
